import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { usePermissions } from "hooks/usePermissions";
import { useViewAs } from "hooks/useViewAs";
import { ACTIONS } from "utils/access";
import {
  canOpenLegacyIntegrations,
  canSeeIntegrations,
  integrationsHomePath,
} from "utils/roles";

export type IntegrationsExperienceKind = "legacy" | "platform";

type Props = {
  /** Which Integrations UI the wrapped routes belong to. */
  experience: IntegrationsExperienceKind;
  children: ReactNode;
};

/**
 * Sends a user to the Integrations UI their role selects (CAL-5777).
 *
 * Internal users with the Integrations Platform page and client portal users
 * may open /integrations-beta*; everyone else with the Integrations role opens
 * the legacy /integrations*. A user on the wrong side is redirected to their
 * own Integrations home, a user without the role to the home page. The VIEWED
 * profile decides, so inside Client View the shell shows exactly what the
 * client would get - when the viewer holds the platform too. Login and
 * password-reset checks stay in the route guards that wrap this gate.
 */
export default function IntegrationsExperienceGate({
  experience,
  children,
}: Props) {
  const { profile, href } = useViewAs();
  const { sees } = usePermissions();
  const allowed =
    experience === "platform"
      ? sees(ACTIONS.INTEGRATIONS_PLATFORM_PORTAL)
      : canOpenLegacyIntegrations(profile);
  if (allowed) return <>{children}</>;
  const target = canSeeIntegrations(profile)
    ? integrationsHomePath(profile)
    : "/";
  return <Navigate to={href(target)} replace />;
}
